import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, IconButton, Button, Box, Chip, Typography, Tooltip, Select, MenuItem, Checkbox
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { exportRowsToExcel, safeFileNamePart } from '../utils/exportExcel';
import { Activity, ProjectActivity } from '../types/ActivityTypes';
import { Project } from '../types/projectTypes';
import { ProjectStatistics } from '../types/projectStatisticsType';
import { getProjectById, getStatistics } from '../api/projectApi';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import { classifyActivity, ActivityCategory, computeActivityGoals, computeRoadAdvance, GoalField } from '../utils/activityGoals';

const EMPTY_AUTO: Record<GoalField, boolean> = {
  productionGoal: false, performacePo: false, performaceHmPo: false,
  performaceUnitGoal: false, performanceUnitPo: false, performanceUnitHmPo: false,
};

/** Convierte un texto a número aceptando coma o punto como separador decimal. */
const toNumber = (raw: string | number | undefined | null): number => {
  if (raw === undefined || raw === null) return 0;
  const s = String(raw).trim().replace(/\s/g, '').replace(',', '.');
  if (s === '') return 0;
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
};

// Fases cuyos pesos deben sumar 100% (las que distribuyen el objetivo de fase).
const PHASE_CATEGORIES: ActivityCategory[] = ['OBRAS_CIVILES', 'MONTAJE', 'TENDIDO_CONDUCTOR', 'TENDIDO_OPGW'];
const PHASE_LABELS: Record<string, string> = {
  OBRAS_CIVILES: 'Obras Civiles',
  MONTAJE: 'Montaje',
  TENDIDO_CONDUCTOR: 'Tendido Conductor',
  TENDIDO_OPGW: 'Tendido OPGW',
};
const WEIGHT_TOLERANCE = 0.5; // margen para considerar "100%"

// Color por fase para identificar visualmente qué actividades deben sumar 100% juntas.
const PHASE_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  OBRAS_CIVILES: { bg: '#dbeafe', border: '#93c5fd', text: '#1e40af' }, // azul
  MONTAJE: { bg: '#dcfce7', border: '#86efac', text: '#166534' }, // verde
  TENDIDO_CONDUCTOR: { bg: '#ffedd5', border: '#fdba74', text: '#9a3412' }, // naranjo
  TENDIDO_OPGW: { bg: '#f3e8ff', border: '#d8b4fe', text: '#6b21a8' }, // morado
};

interface ProjectActivityTableProps {
  projectActivities: ProjectActivity[];
  onUpdate: (updatedActivity: ProjectActivity) => Promise<void> | void;
  onDelete: (id: number) => void;
  activities: Activity[];
  onReorder: (reorderedActivities: ProjectActivity[]) => void;
  onSave: (activities: ProjectActivity[]) => void;
  onCloseClick: () => void;
  fetchLatestActivities: () => void;
  project: Project;
}

// --- ESTILOS GLOBALES EXTERNOS ---
const spreadsheetInputSx = {
  width: '100%',
  '& .MuiInputBase-root': {
    fontSize: '0.75rem', padding: '0px', fontFamily: 'monospace', backgroundColor: 'transparent'
  },
  '& .MuiInputBase-input': {
    padding: '4px 6px', textAlign: 'right', borderRadius: '4px', border: '1px solid transparent', transition: 'all 0.1s',
    '&:hover': { border: '1px solid #cbd5e1', backgroundColor: '#f8fafc' },
    '&:focus': { border: '1px solid #0ea5e9', backgroundColor: '#ffffff', outline: 'none', boxShadow: '0 0 0 2px rgba(14, 165, 233, 0.1)' }
  },
  '& .MuiInput-underline:before': { display: 'none' },
  '& .MuiInput-underline:after': { display: 'none' },
};

const headerStyle = {
  backgroundColor: "#f8fafc", color: "#475569", fontWeight: "600", textTransform: "uppercase" as const,
  fontSize: "0.62rem", letterSpacing: "0.5px", borderBottom: "1px solid #cbd5e1", padding: "10px 8px",
  lineHeight: "1.2", position: "sticky" as const, top: 0, zIndex: 10,
};

const cellStyle = { padding: "6px 8px", fontSize: "0.75rem", color: "#334155", borderBottom: "1px solid #e2e8f0", verticalAlign: "middle" };

// Columnas de Oferta: se distinguen con fondo celeste porque son valores contractuales
// cargados a mano, no metas recalculadas por el sistema.
const offerHeaderStyle = { ...headerStyle, backgroundColor: "#e0f2fe", color: "#075985" };
const offerCellStyle = { ...cellStyle, backgroundColor: "#f0f9ff" };
const rowStyle = { backgroundColor: "#ffffff", transition: "background-color 0.1s ease", "&:hover": { backgroundColor: "#f1f5f9" } };

// --- COMPONENTE CELDA EDITABLE (Elimina el lag al escribir) ---
const EditableCell = React.memo(({ initialValue, field, actId, disabled, endAdornment, onBlurSave }: any) => {
  const [localValue, setLocalValue] = useState<string | number>(initialValue === 0 || initialValue === undefined ? '' : initialValue);

  useEffect(() => {
    setLocalValue(initialValue === 0 || initialValue === undefined ? '' : initialValue);
  }, [initialValue]);

  if (disabled) {
    // Celda automática (o no aplica): se muestra el valor calculado, no editable.
    const shown = initialValue === 0 || initialValue === undefined || initialValue === null || initialValue === ''
      ? '-'
      : String(initialValue).replace('.', ',');
    return <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem', textAlign: 'right', pr: 1, fontFamily: 'monospace' }}>{shown}</Typography>;
  }

  const handleBlur = () => {
    onBlurSave(actId, field, String(localValue));
  };

  return (
    <TextField
      variant="standard"
      value={localValue}
      // Acepta solo dígitos y un separador decimal (coma o punto).
      onChange={(e) => setLocalValue(e.target.value.replace(/[^0-9.,-]/g, ''))}
      onBlur={handleBlur}
      sx={spreadsheetInputSx}
      type="text"
      inputProps={{ inputMode: 'decimal' }}
      InputProps={{ endAdornment: endAdornment ? <span style={{fontSize:'0.65rem', color:'#94a3b8', marginLeft:'2px'}}>{endAdornment}</span> : null }}
    />
  );
});


const ProjectActivityTable: React.FC<ProjectActivityTableProps> = ({
  onReorder,
  projectActivities = [],
  onDelete,
  onSave,
  onCloseClick,
  fetchLatestActivities,
  project
}) => {
  const [localActivities, setLocalActivities] = useState<ProjectActivity[]>([]);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [activityId, setactivityId] = useState(0);
  const [activityName, setActivityName] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [showOfferColumns, setShowOfferColumns] = useState(false);
  const [projectFull, setProjectFull] = useState<Project | null>(null);
  const [stats, setStats] = useState<ProjectStatistics | null>(null);

  useEffect(() => {
    setLocalActivities(projectActivities);
  }, [projectActivities]);

  // Trae el proyecto completo (objetivos de fase) y las estadísticas (ratios) del proyecto.
  useEffect(() => {
    const pid = project?.id ?? projectActivities[0]?.project?.id;
    if (!pid) return;
    getProjectById(pid).then(setProjectFull).catch((e) => console.warn('No se pudo cargar el proyecto (objetivos):', e));
    getStatistics(pid).then(setStats).catch((e) => console.warn('No se pudieron cargar las estadísticas (ratios):', e));
  }, [project?.id, projectActivities]);

  /** Recalcula las metas automáticas de una actividad (producción de fase y/o avance por ratio). */
  const computeFor = useCallback((act: ProjectActivity): { act: ProjectActivity; auto: Record<GoalField, boolean> } => {
    if (!projectFull || !stats || !act?.activity) return { act, auto: EMPTY_AUTO };
    const { values, auto } = computeActivityGoals(act.activity, Number(act.weightDistribution || 0), projectFull, stats);
    let merged: ProjectActivity = { ...act, ...values };
    if (classifyActivity(act.activity) === 'CAMINOS') {
      const adv = computeRoadAdvance(
        act.activity,
        { productionGoal: merged.productionGoal, performacePo: merged.performacePo, performaceHmPo: merged.performaceHmPo },
        stats,
      );
      merged = { ...merged, ...adv };
    }
    return { act: merged, auto };
  }, [projectFull, stats]);

  // Mapa de qué campos son automáticos (read-only) por actividad.
  const autoMap = useMemo(() => {
    const m: Record<number, Record<GoalField, boolean>> = {};
    for (const a of localActivities) if (a?.activity) m[a.id] = computeFor(a).auto;
    return m;
  }, [localActivities, computeFor]);

  // Cuando llegan objetivos + ratios, calcula las metas automáticas de todas las actividades.
  useEffect(() => {
    if (projectFull && stats) {
      setLocalActivities((prev) => prev.map((a) => (a?.activity ? computeFor(a).act : a)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectFull, stats]);

  const dependencyOptions = useMemo(() => {
    return Array.from(new Map(localActivities.map((a) => [a.activity.name, a])).values());
  }, [localActivities]);

  // Suma de pesos por fase (solo las fases que distribuyen el objetivo) para validar el 100%.
  const phaseWeightTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const act of localActivities) {
      if (!act?.activity) continue;
      const category = classifyActivity(act.activity);
      if (!PHASE_CATEGORIES.includes(category)) continue;
      totals[category] = (totals[category] || 0) + Number(act.weightDistribution || 0);
    }
    return totals;
  }, [localActivities]);

  const invalidPhases = useMemo(
    () => Object.entries(phaseWeightTotals).filter(([, sum]) => Math.abs(sum - 100) > WEIGHT_TOLERANCE),
    [phaseWeightTotals]
  );

  /**
   * Exporta la planilla a Excel con las mismas columnas y valores que se ven en pantalla.
   * Los ceros se dejan en blanco igual que en la tabla, donde se muestran como "-".
   */
  const handleExportExcel = () => {
    const exportable = localActivities.filter(act => act && act.activity);
    if (exportable.length === 0) {
      window.alert('No hay actividades para exportar.');
      return;
    }

    const blankIfZero = (value?: number) => (value ? value : '');
    const dashIfNA = (unit?: string) => (unit && unit !== 'NA' ? unit : '');

    const rows = exportable.map((act) => {
      const dependency = act.activityIdDependency
        ? localActivities.find(a => Number(a.activity.id) === Number(act.activityIdDependency))?.activity.name
          || `ID ${act.activityIdDependency}`
        : '';
      const category = classifyActivity(act.activity);

      return {
        'Id': act.id,
        'Fase': PHASE_LABELS[category] || category,
        'Etapa': act.activity.section || '',
        'Actividad': act.activity.name || '',
        'Sub-actividad': act.activity.subActivity || '',
        'Dependencia': dependency,
        'Un. Prod.': dashIfNA(act.activity.unitOfMeasurementProduction),
        'Un. Avance': dashIfNA(act.activity.unitOfMeasurement),
        'Obj. Prod.': blankIfZero(act.productionGoal),
        'Rend. P0 (HH/Prod.)': blankIfZero(act.performacePo),
        'Rend. P0 (HM/Prod.)': blankIfZero(act.performaceHmPo),
        'Obj. Avance': blankIfZero(act.performaceUnitGoal),
        'Rend. P0 (HH/Avance)': blankIfZero(act.performanceUnitPo),
        'Rend. P0 (HM/Avance)': blankIfZero(act.performanceUnitHmPo),
        // Columnas de Oferta: van siempre al Excel, aunque en pantalla esten ocultas
        // tras el toggle "Mostrar Columnas de Oferta". Son valores contractuales y
        // omitirlas dejaria el export incompleto sin que se note.
        'Prod./Día Oferta': blankIfZero(act.offerProductionGoal),
        'Rend. HH Oferta': blankIfZero(act.offerPerformacePo),
        'Rend. HM Oferta': blankIfZero(act.offerPerformaceHmPo),
        'Peso (%)': act.weightDistribution ?? 0,
        'Torres Específicas': act.activityTower ? 'Sí' : 'No',
      };
    });

    exportRowsToExcel(rows, 'Actividades', `Actividades_${safeFileNamePart(project?.name, 'Proyecto')}.xlsx`);
  };

  const handleSaveMatrix = () => {
    // Bloqueo duro: no se permite guardar si alguna fase no suma exactamente 100%.
    if (invalidPhases.length > 0) {
      const detalle = invalidPhases
        .map(([cat, sum]) => `• ${PHASE_LABELS[cat] || cat}: ${Number(sum.toFixed(2))}%`)
        .join('\n');
      window.alert(
        `No se puede guardar.\n\nLos pesos de cada fase deben sumar exactamente 100%. ` +
        `Las siguientes fases aún no lo cumplen:\n\n${detalle}\n\nAjusta los pesos e intenta nuevamente.`
      );
      return;
    }
    setIsEditMode(false);
    onSave(localActivities);
  };

  const handleBulkChange = useCallback((id: number, field: keyof ProjectActivity, value: string | boolean) => {
    let parsedValue: any = value;
    if (typeof value === 'boolean') {
      parsedValue = value;
    } else if (field === 'activityIdDependency') {
      parsedValue = value === '' ? 0 : Number(value);
    } else {
      parsedValue = toNumber(value); // acepta coma o punto como decimal
    }
    setLocalActivities(prev => prev.map(act => {
      if (act.id !== id) return act;
      let next: ProjectActivity = { ...act, [field]: parsedValue };
      // Recalcular metas automáticas en vivo al cambiar el peso (o H/I/J de Caminos).
      if (field === 'weightDistribution') {
        next = computeFor(next).act;
      } else if (classifyActivity(next.activity) === 'CAMINOS' &&
                 (field === 'productionGoal' || field === 'performacePo' || field === 'performaceHmPo')) {
        const adv = computeRoadAdvance(
          next.activity,
          { productionGoal: next.productionGoal, performacePo: next.performacePo, performaceHmPo: next.performaceHmPo },
          stats,
        );
        next = { ...next, ...adv };
      }
      return next;
    }));
  }, [computeFor, stats]);

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;
    const reorderedActivities = [...localActivities];
    const [removed] = reorderedActivities.splice(result.source.index, 1);
    reorderedActivities.splice(result.destination.index, 0, removed);
    for (let i = 0; i < reorderedActivities.length; i++) {
      reorderedActivities[i].order = i + 1;
    }
    setLocalActivities(reorderedActivities);
    onReorder(reorderedActivities);
  };

  const handleDeleteClick = () => onDelete(activityId);

  const handleOpenConfirmModal = (id: number, name: any) => {
    setactivityId(id);
    setActivityName(name);
    setIsConfirmModalOpen(true)
  }

  const MetricValue = ({ val, unit }: { val?: number, unit?: string }) => {
    if (!val) return <Typography sx={{color: '#94a3b8', fontSize: '0.75rem', textAlign: 'right'}}>-</Typography>;
    return (
      <Box display="flex" justifyContent="flex-end" alignItems="baseline" gap={0.5}>
        <Typography sx={{color: '#0f172a', fontSize: '0.75rem', fontWeight: 500, fontFamily: 'monospace'}}>{val}</Typography>
        {unit && <Typography sx={{fontSize:'0.65rem', color:'#64748b'}}>{unit}</Typography>}
      </Box>
    );
  };

  return (
    <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden', backgroundColor: '#ffffff' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, py: 1.5, backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
        <Button
          variant="contained"
          onClick={() => setShowOfferColumns((prev) => !prev)}
          sx={{
            textTransform: 'none', borderRadius: '6px', fontWeight: 600, fontSize: '0.8rem', boxShadow: 'none',
            backgroundColor: showOfferColumns ? '#475569' : '#0284c7',
            '&:hover': { backgroundColor: showOfferColumns ? '#334155' : '#0369a1' },
          }}
        >
          {showOfferColumns ? 'Ocultar Columnas de Oferta' : 'Mostrar Columnas de Oferta'}
        </Button>
      </Box>
      <DragDropContext onDragEnd={isEditMode ? handleDragEnd : () => {}}>
        <Droppable droppableId="droppable">
          {(provided) => (
            <TableContainer ref={provided.innerRef} {...provided.droppableProps} sx={{ maxHeight: "75vh", overflowY: "auto", overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: showOfferColumns ? 1700 : 1400, borderCollapse: 'collapse' }}>
                <TableHead>
                  <TableRow>
                    {isEditMode && <TableCell sx={{...headerStyle, width: '30px'}}></TableCell>}
                    <TableCell sx={{...headerStyle, width: '40px'}}>Id</TableCell>
                    <TableCell sx={headerStyle}>Etapa</TableCell>
                    <TableCell sx={headerStyle}>Actividad</TableCell>
                    <TableCell sx={headerStyle}>Dependencia</TableCell>

                    {/* --- UNIDADES --- */}
                    <TableCell sx={headerStyle}>Un. Prod.</TableCell>
                    <TableCell sx={headerStyle}>Un. Avance</TableCell>

                    {/* --- GRUPO OFERTA (desplegable) --- */}
                    {showOfferColumns && (
                      <>
                        <TableCell align="right" sx={offerHeaderStyle}>Prod./Día Oferta</TableCell>
                        <TableCell align="right" sx={offerHeaderStyle}>Rend. HH Oferta</TableCell>
                        <TableCell align="right" sx={offerHeaderStyle}>Rend. HM Oferta</TableCell>
                      </>
                    )}

                    {/* --- GRUPO PRODUCCIÓN --- */}
                    <TableCell align="right" sx={headerStyle}>Obj. Prod.</TableCell>
                    <TableCell align="right" sx={headerStyle}>Rend. P0 (HH/Prod.)</TableCell>
                    <TableCell align="right" sx={headerStyle}>Rend. P0 (HM/Prod.)</TableCell>

                    {/* --- GRUPO AVANCE --- */}
                    <TableCell align="right" sx={headerStyle}>Obj. Avance</TableCell>
                    <TableCell align="right" sx={headerStyle}>Rend. P0 (HH/Avance)</TableCell>
                    <TableCell align="right" sx={headerStyle}>Rend. P0 (HM/Avance)</TableCell>

                    <TableCell align="right" sx={{...headerStyle, width: '70px'}}>Peso</TableCell>
                    <TableCell align="center" sx={{...headerStyle, width: '80px', lineHeight: '1'}}>Torres<br/>Específicas</TableCell>
                    {isEditMode && <TableCell align="center" sx={headerStyle}>Eliminar</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {localActivities.filter(act => act && act.activity).map((activity, index) => (
                    <Draggable key={activity.id} draggableId={String(activity.id)} index={index} isDragDisabled={!isEditMode}>
                      {(providedDraggable, snapshot) => (
                        <TableRow
                          ref={providedDraggable.innerRef}
                          {...providedDraggable.draggableProps}
                          sx={{
                            ...rowStyle,
                            ...(snapshot.isDragging && { backgroundColor: '#f8fafc', boxShadow: 'inset 0 0 0 1px #0ea5e9', opacity: 0.9 })
                          }}
                        >
                          {isEditMode && (
                            <TableCell sx={{...cellStyle, px: 0, textAlign: 'center'}} {...providedDraggable.dragHandleProps}>
                              <DragIndicatorIcon sx={{ color: '#cbd5e1', cursor: 'grab', fontSize: '1.2rem' }} />
                            </TableCell>
                          )}

                          <TableCell sx={{...cellStyle, color: '#64748b', fontFamily: 'monospace'}}>{activity.id}</TableCell>

                          <TableCell sx={cellStyle}><Typography sx={{fontSize: '0.70rem', color: '#475569', fontWeight: 500}}>{activity.activity.section}</Typography></TableCell>
                          <TableCell sx={{...cellStyle, maxWidth: '200px'}}>
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#0f172a' }}>{activity.activity.name}</Typography>
                            {activity.activity.subActivity && <Typography sx={{ fontSize: '0.65rem', color: '#64748b' }}>{activity.activity.subActivity}</Typography>}
                          </TableCell>

                          {/* DEPENDENCIA */}
                          <TableCell sx={{...cellStyle, maxWidth: '140px'}}>
                            {isEditMode ? (
                              <Select
                                variant="standard"
                                disableUnderline
                                value={activity.activityIdDependency ?? 0}
                                onChange={(e) => handleBulkChange(activity.id, 'activityIdDependency', String(e.target.value))}
                                sx={{ fontSize: '0.70rem', color: '#0f172a', width: '100%', '& .MuiSelect-select': { padding: '4px', borderRadius: '4px', '&:hover': { backgroundColor: '#f8fafc' } } }}
                              >
                                <MenuItem value={0} sx={{fontSize: '0.75rem'}}>Sin dependencia</MenuItem>
                                {dependencyOptions.filter(a => a.id !== activity.id).map((a) => (
                                  <MenuItem key={a.id} value={a.activity.id} sx={{fontSize: '0.75rem'}}>{a.activity.name}</MenuItem>
                                ))}
                              </Select>
                            ) : (
                              (() => {
                                if (!activity.activityIdDependency || activity.activityIdDependency === 0) return <Typography sx={{color:'#94a3b8', fontSize: '0.75rem'}}>-</Typography>;
                                const dep = localActivities.find((a) => Number(a.activity.id) === Number(activity.activityIdDependency));
                                return <Tooltip title="Actividad Precedente"><Chip label={dep?.activity.name || `ID ${activity.activityIdDependency}`} size="small" sx={{fontSize: '0.65rem', height: '20px', backgroundColor: '#f1f5f9', color: '#475569'}} /></Tooltip>;
                              })()
                            )}
                          </TableCell>

                          {/* UNIDADES (Nuevas columnas) */}
                          <TableCell sx={cellStyle}>
                            <Typography sx={{ fontSize: '0.70rem', color: '#475569', fontWeight: 500 }}>
                              {activity.activity.unitOfMeasurementProduction !== "NA" ? activity.activity.unitOfMeasurementProduction : "-"}
                            </Typography>
                          </TableCell>
                          <TableCell sx={cellStyle}>
                            <Typography sx={{ fontSize: '0.70rem', color: '#475569', fontWeight: 500 }}>
                              {activity.activity.unitOfMeasurement !== "NA" ? activity.activity.unitOfMeasurement : "-"}
                            </Typography>
                          </TableCell>

                          {/* --- MÉTRICAS: GRUPO OFERTA (siempre manual, nunca automática) --- */}
                          {showOfferColumns && (
                            <>
                              <TableCell align="right" sx={offerCellStyle}>
                                {isEditMode
                                  ? <EditableCell initialValue={activity.offerProductionGoal} field="offerProductionGoal" actId={activity.id} disabled={!activity.activity.addProduction} onBlurSave={handleBulkChange} />
                                  : <MetricValue val={activity.offerProductionGoal} />}
                              </TableCell>
                              <TableCell align="right" sx={offerCellStyle}>
                                {isEditMode
                                  ? <EditableCell initialValue={activity.offerPerformacePo} field="offerPerformacePo" actId={activity.id} disabled={!activity.activity.addMh} onBlurSave={handleBulkChange} />
                                  : <MetricValue val={activity.offerPerformacePo} />}
                              </TableCell>
                              <TableCell align="right" sx={offerCellStyle}>
                                {isEditMode
                                  ? <EditableCell initialValue={activity.offerPerformaceHmPo} field="offerPerformaceHmPo" actId={activity.id} disabled={!activity.activity.addMh} onBlurSave={handleBulkChange} />
                                  : <MetricValue val={activity.offerPerformaceHmPo} />}
                              </TableCell>
                            </>
                          )}

                          {/* --- MÉTRICAS: GRUPO PRODUCCIÓN --- */}
                          <TableCell align="right" sx={cellStyle}>
                            {isEditMode
                              ? <EditableCell initialValue={activity.productionGoal} field="productionGoal" actId={activity.id} disabled={!activity.activity.addProduction || (autoMap[activity.id]?.productionGoal ?? false)} onBlurSave={handleBulkChange} />
                              : <MetricValue val={activity.productionGoal} />}
                          </TableCell>

                          <TableCell align="right" sx={cellStyle}>
                            {isEditMode ? <EditableCell initialValue={activity.performacePo} field="performacePo" actId={activity.id} disabled={!activity.activity.addMh || (autoMap[activity.id]?.performacePo ?? false)} onBlurSave={handleBulkChange} /> : <MetricValue val={activity.performacePo} />}
                          </TableCell>

                          <TableCell align="right" sx={cellStyle}>
                            {isEditMode ? <EditableCell initialValue={activity.performaceHmPo} field="performaceHmPo" actId={activity.id} disabled={!activity.activity.addMh || (autoMap[activity.id]?.performaceHmPo ?? false)} onBlurSave={handleBulkChange} /> : <MetricValue val={activity.performaceHmPo} />}
                          </TableCell>

                          {/* --- MÉTRICAS: GRUPO AVANCE --- */}
                          <TableCell align="right" sx={cellStyle}>
                            {isEditMode
                              ? <EditableCell initialValue={activity.performaceUnitGoal} field="performaceUnitGoal" actId={activity.id} disabled={activity.activity.unitOfMeasurement === 'NA' || (autoMap[activity.id]?.performaceUnitGoal ?? false)} onBlurSave={handleBulkChange} />
                              : <MetricValue val={activity.performaceUnitGoal} />}
                          </TableCell>

                          <TableCell align="right" sx={cellStyle}>
                            {isEditMode ? <EditableCell initialValue={activity.performanceUnitPo} field="performanceUnitPo" actId={activity.id} disabled={activity.activity.unitOfMeasurement === 'NA' || (autoMap[activity.id]?.performanceUnitPo ?? false)} onBlurSave={handleBulkChange} /> : <MetricValue val={activity.performanceUnitPo} />}
                          </TableCell>

                          <TableCell align="right" sx={cellStyle}>
                            {isEditMode ? <EditableCell initialValue={activity.performanceUnitHmPo} field="performanceUnitHmPo" actId={activity.id} disabled={activity.activity.unitOfMeasurement === 'NA' || (autoMap[activity.id]?.performanceUnitHmPo ?? false)} onBlurSave={handleBulkChange} /> : <MetricValue val={activity.performanceUnitHmPo} />}
                          </TableCell>

                          {/* DISTRIBUCIÓN DE PESO (coloreado por fase) */}
                          {(() => {
                            const phaseColor = PHASE_COLORS[classifyActivity(activity.activity)];
                            return (
                              <TableCell
                                align="right"
                                sx={{
                                  ...cellStyle,
                                  ...(phaseColor && { backgroundColor: phaseColor.bg, borderLeft: `2px solid ${phaseColor.border}` }),
                                }}
                              >
                                {isEditMode
                                  ? <EditableCell initialValue={activity.weightDistribution} field="weightDistribution" actId={activity.id} disabled={false} endAdornment="%" onBlurSave={handleBulkChange} />
                                  : <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: phaseColor ? phaseColor.text : (activity.weightDistribution > 0 ? '#0f172a' : '#94a3b8'), fontFamily: 'monospace' }}>{activity.weightDistribution}%</Typography>}
                              </TableCell>
                            );
                          })()}

                          {/* CHECKBOX DE TORRES ESPECÍFICAS */}
                          <TableCell align="center" sx={cellStyle}>
                            <Checkbox
                              size="small"
                              checked={Boolean(activity.activityTower)}
                              disabled={!isEditMode}
                              onChange={(e) => handleBulkChange(activity.id, 'activityTower', e.target.checked)}
                              sx={{
                                color: '#cbd5e1',
                                '&.Mui-checked': { color: '#0ea5e9' },
                                padding: '4px'
                              }}
                            />
                          </TableCell>

                          {isEditMode && (
                            <TableCell align="center" sx={cellStyle}>
                              <IconButton onClick={() => handleOpenConfirmModal(activity.id, activity.activity.name)} size="small" sx={{ color: '#94a3b8', '&:hover': {color: '#ef4444', backgroundColor: '#fee2e2'} }}>
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      )}
                    </Draggable>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Droppable>
      </DragDropContext>

      {/* Resumen de peso por fase (debe sumar 100% en cada una) */}
      {isEditMode && Object.keys(phaseWeightTotals).length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', px: 2, py: 1.5, backgroundColor: '#ffffff', borderTop: '1px solid #e2e8f0' }}>
          <Typography sx={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600, mr: 0.5 }}>Peso por fase:</Typography>
          {PHASE_CATEGORIES.filter((c) => phaseWeightTotals[c] !== undefined).map((cat) => {
            const sum = phaseWeightTotals[cat];
            const ok = Math.abs(sum - 100) <= WEIGHT_TOLERANCE;
            const colors = PHASE_COLORS[cat];
            return (
              <Chip
                key={cat}
                size="small"
                label={`${PHASE_LABELS[cat]}: ${Number(sum.toFixed(2))}% ${ok ? '✓' : '⚠'}`}
                sx={{
                  fontSize: '0.68rem', fontWeight: 600,
                  backgroundColor: colors.bg,
                  color: colors.text,
                  border: ok ? `1px solid ${colors.border}` : '1px solid #ef4444',
                }}
              />
            );
          })}
          {invalidPhases.length > 0 && (
            <Typography sx={{ fontSize: '0.7rem', color: '#b91c1c', fontWeight: 600 }}>
              ⚠ No se puede guardar hasta que cada fase sume 100%.
            </Typography>
          )}
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
        <Typography sx={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>{localActivities.length} actividades registradas</Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={handleExportExcel}
            sx={{ textTransform: 'none', borderRadius: '6px', borderColor: '#cbd5e1', color: '#0f172a', fontWeight: 600, fontSize: '0.8rem', backgroundColor: '#ffffff', '&:hover': { backgroundColor: '#f1f5f9' } }}
          >
            Exportar Excel
          </Button>
          {!isEditMode ? (
            <>
              <Button variant="text" onClick={onCloseClick} sx={{ textTransform: 'none', color: '#475569', fontWeight: 600, fontSize: '0.8rem' }}>Cerrar</Button>
              <Button variant="outlined" sx={{ textTransform: 'none', borderRadius: '6px', borderColor: '#cbd5e1', color: '#0f172a', fontWeight: 600, fontSize: '0.8rem', backgroundColor: '#ffffff', '&:hover': { backgroundColor: '#f1f5f9' } }}
                onClick={async () => { await fetchLatestActivities(); setIsEditMode(true); }}>
                Habilitar Planilla (Editar)
              </Button>
            </>
          ) : (
            <>
              <Button variant="text" sx={{ textTransform: 'none', color: '#64748b', fontWeight: 600, fontSize: '0.8rem' }}
                onClick={async () => { await fetchLatestActivities(); setIsEditMode(false); }}>
                Descartar Cambios
              </Button>
              <Tooltip title={invalidPhases.length > 0 ? 'Cada fase debe sumar 100% en la columna Peso' : ''}>
                <span>
                  <Button variant="contained" disabled={invalidPhases.length > 0}
                    sx={{ textTransform: 'none', borderRadius: '6px', backgroundColor: '#0f172a', color: '#ffffff', fontWeight: 600, fontSize: '0.8rem', '&:hover': { backgroundColor: '#334155' }, boxShadow: 'none', '&.Mui-disabled': { backgroundColor: '#cbd5e1', color: '#ffffff' } }}
                    onClick={handleSaveMatrix}>
                    Guardar Matriz Completa
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>
      <ConfirmDeleteModal open={isConfirmModalOpen} onClose={() => setIsConfirmModalOpen(false)} onSubmit={handleDeleteClick} id={activityId} name={activityName} title={'PROJECTACTIVITIES'} />
    </Box>
  );
};

export default ProjectActivityTable;